import { EditIcon, TrashIcon } from 'lucide-react';
import type React from 'react';
import { useEffect, useState } from 'react';
import { FormDetailPane } from '@/components/common/FormDetailPane';
import { SelectFormField, TextFormField } from '@/components/common/FormField';
import { Button } from '@/components/ui/button';
import { FieldGroup } from '@/components/ui/field';
import { handleApiError } from '@/lib/errors';
import type {
  ProjectPropertyFieldDef,
  ProjectPropertyRow,
  ProjectPropertyTableConfig,
} from '@/lib/projectPropertiesConfig';
import {
  type ProjectPropertyInput,
  projectPropertiesService,
} from '@/services/projectPropertiesService';
import { logger } from '@/utils/logger';

function emptyForm(config: ProjectPropertyTableConfig): Record<string, string> {
  const form: Record<string, string> = {};
  for (const field of config.fields) {
    form[field.key] = '';
  }
  return form;
}

function rowToForm(
  config: ProjectPropertyTableConfig,
  row: ProjectPropertyRow
): Record<string, string> {
  const form = emptyForm(config);
  for (const field of config.fields) {
    const value = row[field.key];
    form[field.key] = value != null ? String(value) : '';
  }
  return form;
}

function formToPayload(
  config: ProjectPropertyTableConfig,
  form: Record<string, string>
): ProjectPropertyInput {
  const payload: Record<string, string | undefined> = {};
  for (const field of config.fields) {
    const trimmed = (form[field.key] ?? '').trim();
    payload[field.key] = trimmed || undefined;
  }
  return payload as ProjectPropertyInput;
}

function renderField(
  field: ProjectPropertyFieldDef,
  form: Record<string, string>,
  onChange: (key: string, value: string) => void,
  readOnly: boolean
) {
  const value = form[field.key] ?? '';
  if (field.type === 'select' && field.options) {
    return (
      <SelectFormField
        key={field.key}
        label={field.label}
        value={value}
        onChange={(v) => onChange(field.key, v)}
        options={field.options}
        disabled={readOnly}
        copyable={readOnly}
      />
    );
  }
  return (
    <TextFormField
      key={field.key}
      label={field.label}
      value={value}
      onChange={(v) => onChange(field.key, v)}
      disabled={readOnly}
      copyable={readOnly}
    />
  );
}

interface ProjectPropertyFormPaneProps {
  config: ProjectPropertyTableConfig;
  isOpen: boolean;
  onClose: () => void;
  onSave: (row: ProjectPropertyRow) => void;
  onEntrySaved?: () => void;
  row?: ProjectPropertyRow | null;
  isEdit?: boolean;
  readOnly?: boolean;
  onEditRequest?: () => void;
  onDeleteRequest?: () => void;
}

export const ProjectPropertyFormPane: React.FC<ProjectPropertyFormPaneProps> = ({
  config,
  isOpen,
  onClose,
  onSave,
  onEntrySaved,
  row,
  isEdit = false,
  readOnly = false,
  onEditRequest,
  onDeleteRequest,
}) => {
  const [form, setForm] = useState<Record<string, string>>(() => emptyForm(config));
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);

  useEffect(() => {
    if (!isOpen) return;
    setForm((isEdit || readOnly) && row ? rowToForm(config, row) : emptyForm(config));
    setSubmitError(null);
  }, [isOpen, isEdit, readOnly, row, config]);

  const handleChange = (key: string, value: string) => {
    setForm((prev) => ({ ...prev, [key]: value }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitError(null);
    try {
      const payload = formToPayload(config, form);
      const saved =
        isEdit && row
          ? await projectPropertiesService.update(config, row.id, payload)
          : await projectPropertiesService.create(config, payload);
      onSave(saved);
      onEntrySaved?.();
      onClose();
    } catch (error) {
      logger.error('Project property save failed', error);
      setSubmitError(handleApiError(error));
    } finally {
      setIsSubmitting(false);
    }
  };

  const title = readOnly
    ? `View ${config.title}`
    : isEdit
      ? `Edit ${config.title}`
      : `Add ${config.title}`;

  return (
    <FormDetailPane
      open={isOpen}
      onOpenChange={(open) => {
        if (!open) onClose();
      }}
      title={title}
      description={config.description}
      onSubmit={readOnly ? undefined : handleSubmit}
      submitLabel={isEdit ? 'Update' : 'Add'}
      isSubmitting={isSubmitting}
      submitError={submitError}
      showFooter={!readOnly}
    >
      {readOnly ? (
        <div className="flex flex-wrap justify-end gap-2 border-b border-border-default pb-4">
          {onEditRequest ? (
            <Button type="button" size="sm" onClick={onEditRequest}>
              <EditIcon data-icon="inline-start" />
              Edit
            </Button>
          ) : null}
          {onDeleteRequest ? (
            <Button type="button" size="sm" variant="destructive" onClick={onDeleteRequest}>
              <TrashIcon data-icon="inline-start" />
              Delete
            </Button>
          ) : null}
        </div>
      ) : null}
      <FieldGroup>
        {config.fields.map((field) => renderField(field, form, handleChange, readOnly))}
      </FieldGroup>
    </FormDetailPane>
  );
};
